import { createSlice } from '@reduxjs/toolkit';

// Helper function to get random item avoiding recent history
const getRandomItem = (items, currentItem, history, maxHistorySize = 3) => {
  if (items.length <= 1) return items[0];
  
  // Filter out current item and recent history
  const availableItems = items.filter(item => {
    const itemId = item.id || item.versions?.[0]?.id;
    const currentId = currentItem?.id || currentItem?.projectId;
    const recentIds = history.slice(-maxHistorySize);
    
    return itemId !== currentId && !recentIds.includes(itemId);
  });
  
  // If all items are in history, just avoid current item
  const fallbackItems = availableItems.length > 0 ? availableItems : 
    items.filter(item => {
      const itemId = item.id || item.versions?.[0]?.id;
      const currentId = currentItem?.id || currentItem?.projectId;
      return itemId !== currentId;
    });
  
  const finalItems = fallbackItems.length > 0 ? fallbackItems : items;
  return finalItems[Math.floor(Math.random() * finalItems.length)];
};

export const playlist = createSlice({
  name: 'playlist',
  initialState: {
    playlist: [],
    context: null, // 'my-chest' | 'project'
    projectList: [], // All projects for My Chest navigation
    versionsList: [], // All versions for project navigation
    currentProjectId: null,
    shuffle: false,
    shuffleHistory: [] // Track played items to avoid immediate repeats
  },
  reducers: {
    add: (state, action) => {
      state.playlist.push(action.payload);
    },
    remove: (state, action) => {
      state.playlist.splice(state.playlist.findIndex(track => track.name === action.payload), 1);
    },
    playing: (state, action) => {
      state.playlist[0] = action.payload
    },
    play: (state, action) => {
      if (state.playlist.length > 0) {
        state.playlist[0].isPlaying = !state.playlist[0].isPlaying;
      } 
    },
    next: (state) => {
      if (state.context === 'my-chest' && state.projectList.length > 0) {
        const currentTrack = state.playlist[0];
        if (currentTrack) {
          let nextProject;
          
          if (state.shuffle) {
            // Get random project avoiding recent history
            nextProject = getRandomItem(state.projectList, currentTrack, state.shuffleHistory);
            // Add to shuffle history
            state.shuffleHistory.push(currentTrack.projectId);
            if (state.shuffleHistory.length > 5) {
              state.shuffleHistory.shift(); // Keep history manageable
            }
          } else {
            // Sequential navigation
            const currentIndex = state.projectList.findIndex(project => project.id === currentTrack.projectId);
            const nextIndex = (currentIndex + 1) % state.projectList.length;
            nextProject = state.projectList[nextIndex];
          }
          
          // Get latest version of next project
          const latestVersion = nextProject.versions[0]; // Assuming versions are sorted by date desc
          
          const nextTrack = {
            id: latestVersion.id,
            projectId: nextProject.id,
            album: nextProject.album,
            cover: nextProject.cover_url,
            name: nextProject.name,
            authors: nextProject.authors || [],
            type: 'project',
            isPlaying: true
          };
          
          state.playlist = [nextTrack];
        }
      } else if (state.context === 'project' && state.versionsList.length > 0) {
        const currentTrack = state.playlist[0];
        if (currentTrack) {
          let nextVersion;
          
          if (state.shuffle) {
            // Get random version avoiding recent history  
            nextVersion = getRandomItem(state.versionsList, currentTrack, state.shuffleHistory);
            // Add to shuffle history
            state.shuffleHistory.push(currentTrack.id);
            if (state.shuffleHistory.length > 5) {
              state.shuffleHistory.shift();
            }
          } else {
            // Sequential chronological navigation
            const currentIndex = state.versionsList.findIndex(version => version.id === currentTrack.id);
            if (currentIndex >= 0 && currentIndex < state.versionsList.length - 1) {
              nextVersion = state.versionsList[currentIndex + 1];
            } else {
              return; // No next version in sequence
            }
          }
          
          const nextTrack = {
            id: nextVersion.id,
            projectId: state.currentProjectId,
            album: currentTrack.album,
            cover: currentTrack.cover,
            name: currentTrack.name,
            authors: currentTrack.authors,
            type: 'version',
            isPlaying: true
          };
          
          state.playlist = [nextTrack];
        }
      } else {
        // Fallback to old behavior
        if (state.playlist.length > 1) {
          state.playlist.shift();
          state.playlist[0].isPlaying = true;
        }
      }
    },
    previous: (state) => {
      if (state.context === 'my-chest' && state.projectList.length > 0) {
        const currentTrack = state.playlist[0];
        if (currentTrack) {
          let prevProject;
          
          if (state.shuffle && state.shuffleHistory.length > 0) {
            // Go back to last played from history
            const lastPlayedId = state.shuffleHistory.pop();
            prevProject = state.projectList.find(project => project.id === lastPlayedId);
            if (!prevProject) {
              // Fallback to sequential if history item not found
              const currentIndex = state.projectList.findIndex(project => project.id === currentTrack.projectId);
              const prevIndex = currentIndex === 0 ? state.projectList.length - 1 : currentIndex - 1;
              prevProject = state.projectList[prevIndex];
            }
          } else {
            // Sequential navigation
            const currentIndex = state.projectList.findIndex(project => project.id === currentTrack.projectId);
            const prevIndex = currentIndex === 0 ? state.projectList.length - 1 : currentIndex - 1;
            prevProject = state.projectList[prevIndex];
          }
          
          // Get latest version of previous project
          const latestVersion = prevProject.versions[0];
          
          const prevTrack = {
            id: latestVersion.id,
            projectId: prevProject.id,
            album: prevProject.album,
            cover: prevProject.cover_url,
            name: prevProject.name,
            authors: prevProject.authors || [],
            type: 'project',
            isPlaying: true
          };
          
          state.playlist = [prevTrack];
        }
      } else if (state.context === 'project' && state.versionsList.length > 0) {
        const currentTrack = state.playlist[0];
        if (currentTrack) {
          let prevVersion;
          
          if (state.shuffle && state.shuffleHistory.length > 0) {
            // Go back to last played from history
            const lastPlayedId = state.shuffleHistory.pop();
            prevVersion = state.versionsList.find(version => version.id === lastPlayedId);
            if (!prevVersion) {
              // Fallback to sequential
              const currentIndex = state.versionsList.findIndex(version => version.id === currentTrack.id);
              if (currentIndex > 0) {
                prevVersion = state.versionsList[currentIndex - 1];
              } else {
                return; // No previous version
              }
            }
          } else {
            // Sequential chronological navigation
            const currentIndex = state.versionsList.findIndex(version => version.id === currentTrack.id);
            if (currentIndex > 0) {
              prevVersion = state.versionsList[currentIndex - 1];
            } else {
              return; // No previous version in sequence
            }
          }
          
          const prevTrack = {
            id: prevVersion.id,
            projectId: state.currentProjectId,
            album: currentTrack.album,
            cover: currentTrack.cover,
            name: currentTrack.name,
            authors: currentTrack.authors,
            type: 'version',
            isPlaying: true
          };
          
          state.playlist = [prevTrack];
        }
      } else {
        // Fallback to old behavior
        if (state.playlist.length > 1) {
          state.playlist.unshift(state.playlist[state.playlist.length - 1]);
          state.playlist.pop();
          state.playlist[0].isPlaying = true;
        }
      }
    },
    setContext: (state, action) => {
      const { context, projectList, versionsList, currentProjectId } = action.payload;
      state.context = context;
      state.projectList = projectList || [];
      state.versionsList = versionsList || [];
      state.currentProjectId = currentProjectId || null;
    },
    toggleShuffle: (state) => {
      state.shuffle = !state.shuffle;
      // Clear shuffle history when toggling
      state.shuffleHistory = [];
    },
    reset: (state) => { 
      state.playlist = [];
      state.context = null;
      state.projectList = [];
      state.versionsList = [];
      state.currentProjectId = null;
      state.shuffle = false;
      state.shuffleHistory = [];
    },
  }
});

export const { add, remove, playing, reset, play, next, previous, setContext, toggleShuffle } = playlist.actions;

export default playlist.reducer;